import type { ReactNode } from "react";
import {
  Box,
  HStack,
  Icon,
  Image,
  Stack,
  Text,
  Button,
} from "@chakra-ui/react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import type { CreativeBlock } from "../guidedFlow/types";
import {
  hasFacebookPage,
  hasInstagramAccount,
  hasSquareCreativeSlot,
  hasVerticalCreativeSlot,
} from "../guidedFlow/creativePreviewSlots";

export type CreativeBlockReviewProps = {
  creative: CreativeBlock;
  index: number;
  onEdit: () => void;
  launching?: boolean;
};

const imageHeight = "150px" as const;

function MediaSlotBox({
  label,
  aspectLabel,
  children,
  fullWidth,
}: {
  label: string;
  aspectLabel: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <Box
      flex={1}
      aspectRatio={1}
      alignContent="center"
      w={fullWidth ? "full" : "50%"}
      bg="gray.50"
      rounded="lg"
      p={2}
      border="1px dotted"
      borderColor="gray.300"
      textAlign="center"
    >
      <Text fontSize="sm" color="gray.500" fontWeight="semibold" mb={2}>
        <Text as="span" color="gray.600">
          {label}{" "}
        </Text>
        {aspectLabel}
      </Text>
      <Box mx="auto" w="fit-content" shadow="lg">
        {children}
      </Box>
    </Box>
  );
}

export default function CreativeBlockReview({
  creative,
  onEdit,
  launching = false,
}: CreativeBlockReviewProps) {
  const showSquare =
    hasSquareCreativeSlot(creative) && Boolean(creative.squareMediaUrl?.trim());
  const showVertical =
    hasVerticalCreativeSlot(creative) &&
    Boolean(creative.verticalMediaUrl?.trim());
  const singleMediaSlot = showSquare !== showVertical;

  const identityRows = [
    hasFacebookPage(creative)
      ? {
          icon: FaFacebookF,
          label: "FB Page",
          value: creative.fbPage?.name ?? "—",
        }
      : null,
    hasInstagramAccount(creative)
      ? {
          icon: FaInstagram,
          label: "IG Account",
          value: creative.instagramAccount?.username ?? "—",
        }
      : null,
  ].filter((row): row is NonNullable<typeof row> => row != null);

  return (
    <Box
      bg="white"
      rounded="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack>
        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mb={4}>
          {creative.name || "-"}
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Single-format or multi-format
        </Text>
      </HStack>

      {(showSquare || showVertical) && (
        <Stack
          direction={{ base: "column", sm: singleMediaSlot ? "column" : "row" }}
          align={singleMediaSlot ? "center" : "stretch"}
          spacing={4}
          mb={4}
        >
          {showSquare && (
            <MediaSlotBox
              label="Square"
              aspectLabel="1:1"
              fullWidth={!singleMediaSlot}
            >
              {creative.squareMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.squareMediaUrl}
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.squareMediaUrl}
                  alt=""
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                />
              )}
            </MediaSlotBox>
          )}
          {showVertical && (
            <MediaSlotBox
              label="Vertical"
              aspectLabel="16:9"
              fullWidth={!singleMediaSlot}
            >
              {creative.verticalMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.verticalMediaUrl}
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.verticalMediaUrl}
                  alt=""
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                />
              )}
            </MediaSlotBox>
          )}
        </Stack>
      )}

      <Box mb={4}>
        <HStack justify="space-between" align="flex-start" mb={1}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            Primary Text
          </Text>
          <Button
            variant="link"
            fontSize="sm"
            fontWeight="medium"
            color="blue.600"
            onClick={onEdit}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            flexShrink={0}
            isDisabled={launching}
          >
            Edit
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.600" noOfLines={4}>
          {creative.primaryText || "—"}
        </Text>
      </Box>

      {identityRows.map(({ icon: IconComponent, label, value }) => (
        <HStack
          key={label}
          spacing={3}
          flex={1}
          align="center"
          justify="space-between"
          w="full"
          borderTop="1px solid"
          borderColor="gray.200"
          py="2"
        >
          <HStack display="flex" alignContent="center" gap={4}>
            <Box display="flex" alignContent="center" gap={2}>
              <Icon
                as={IconComponent}
                boxSize={4}
                color="gray.600"
                display="inline-block"
              />
              <Text
                fontSize="xs"
                color="gray.500"
                display="inline-block"
                textAlign="left"
                flex={1}
                width="70px"
              >
                {label}
              </Text>
            </Box>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="gray.800"
              noOfLines={1}
            >
              {value}
            </Text>
          </HStack>
          <Button
            variant="link"
            fontSize="sm"
            fontWeight="medium"
            color="blue.600"
            onClick={() => !launching && onEdit()}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            flexShrink={0}
            isDisabled={launching}
          >
            Change
          </Button>
        </HStack>
      ))}
    </Box>
  );
}
